import { useEffect, useState } from "react";
import { Container } from "@mantine/core";
import { AnimatePresence } from "framer-motion";
import type { Message } from "wall/types/message.model";
import { Wall, wallTemplate } from "wall/types/wall.model";
import { default as CoubertinAnimatedMessage } from "./coubertin/AnimatedMessage";
import { default as ParcDesPrincesAnimatedMessage } from "./parc_des_princes/AnimatedMessage";
import { WallContext } from "wall/hooks/useWall";

const MessagesSlideshow = ({
  messages,
  template,
  currentIndex,
  onSlideAdvance,
  slideshowStatus,
  slideshowInterval,
  wall,
}: {
  messages: Message[];
  template: wallTemplate;
  currentIndex: number;
  onSlideAdvance: () => void;
  slideshowStatus: "PLAY" | "PAUSE" | undefined;
  slideshowInterval: number;
  wall: Wall;
}) => {
  const [isRunning, setIsRunning] = useState(false);

  // Determine if slideshow should be running
  const shouldRun =
    messages &&
    messages.length > 1 &&
    (slideshowStatus === "PLAY" || slideshowStatus === undefined) &&
    slideshowInterval &&
    slideshowInterval > 0;

  console.log("=== SLIDESHOW STATE ===");
  console.log("Messages count:", messages?.length);
  console.log("Current index:", currentIndex);
  console.log("Should run:", shouldRun);
  console.log("Slideshow status:", slideshowStatus);
  console.log("Interval:", slideshowInterval);

  // Simple interval-based slideshow
  useEffect(() => {
    if (!shouldRun) {
      setIsRunning(false);
      return;
    }

    setIsRunning(true);
    console.log(`Starting slideshow with ${slideshowInterval}ms interval`);

    const interval = setInterval(() => {
      console.log("Slideshow interval fired, advancing slide...");
      onSlideAdvance();
    }, slideshowInterval);

    return () => {
      console.log("Cleaning up slideshow interval");
      clearInterval(interval);
      setIsRunning(false);
    };
  }, [shouldRun, slideshowInterval, onSlideAdvance]);

  // Choose animation component based on template
  const AnimatedMessage =
    template === "bandeau_horizontal"
      ? CoubertinAnimatedMessage
      : ParcDesPrincesAnimatedMessage;

  // Get current message
  const currentMessage = messages?.[currentIndex];

  // Fallback when no messages
  if (!messages || messages.length === 0) {
    return (
      <Container className="max-w-[1360px] h-full">
        <div className="flex items-center justify-center h-full">
          <div className="text-center text-gray-500">
            <h2 className="text-2xl font-bold mb-2">No messages available</h2>
            <p>Waiting for new messages to display...</p>
          </div>
        </div>
      </Container>
    );
  }

  // Debug display if current message is missing
  if (!currentMessage) {
    return (
      <Container className="max-w-[1360px] h-full">
        <div className="flex items-center justify-center h-full">
          <div className="text-center text-red-500">
            <h2 className="text-2xl font-bold mb-2">Debug Info</h2>
            <p>Index: {currentIndex}</p>
            <p>Messages length: {messages.length}</p>
            <p>Should run: {shouldRun ? "YES" : "NO"}</p>
            <p>Is running: {isRunning ? "YES" : "NO"}</p>
            <p>Interval: {slideshowInterval}ms</p>
            <p>Status: {slideshowStatus || "undefined"}</p>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container
      size={template === "bandeau_horizontal" ? "xl" : undefined}
      className="max-w-[1360px] h-full"
    >
      <div className="relative">
        {/* Debug overlay */}
        {/* <div className="absolute top-2 right-2 bg-black/50 text-white text-xs p-2 rounded z-10">
          <div>
            Index: {currentIndex + 1}/{messages.length}
          </div>
          <div>Running: {isRunning ? "✅" : "❌"}</div>
          <div>Interval: {slideshowInterval}ms</div>
          <div>Status: {slideshowStatus || "auto"}</div>
        </div> */}

        <AnimatePresence mode="wait">
          <WallContext.Provider value={wall}>
            <AnimatedMessage
              key={`${currentIndex}-${currentMessage.id}`}
              message={currentMessage}
            />
          </WallContext.Provider>
        </AnimatePresence>
      </div>
    </Container>
  );
};

export default MessagesSlideshow;
